import { Search as SearchIcon } from '@mui/icons-material';
import { Popover } from '@mui/material';
import type React from 'react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import VNDropdownDisplay from './input/VNDropdownDisplay';
import VNDropdownList from './options/VNDropdownList';
import type { DisplaySlotProps, DropdownOption, ListSlotProps } from './types';
import { filterOptionsWithAncestors } from './types';

export interface VNDropdownBaseProps {
  options: DropdownOption[];
  /** Current selection. Pass a single id for single-select, an array for multi-select. */
  value: DropdownOption['id'] | Array<DropdownOption['id']> | null;
  onChange: (value: DropdownOption['id'] | Array<DropdownOption['id']> | null) => void;
  onBlur?: () => void;
  /** Validation error message — rendered below the trigger and announced via aria live region */
  error?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  multiple?: boolean;
  /** Replace the default trigger display with a custom render slot */
  displayComponent?: React.ComponentType<DisplaySlotProps>;
  /** Replace the default option list with a custom render slot */
  listComponent?: React.ComponentType<ListSlotProps>;
  /** Forwarded ref for programmatic focus (e.g. from react-hook-form) */
  inputRef?: React.Ref<HTMLButtonElement>;
  /** Remove the border from the trigger button */
  disableBorder?: boolean;
  /** Compact mode: fixed 121px trigger with no border, count badge instead of chips */
  compact?: boolean;
  /** Override the popover paper width in pixels */
  popoverWidth?: number;
  /** Override the popover paper minimum height in pixels */
  popoverMinHeight?: number;
  /** Additional CSS class(es) applied to the root element */
  className?: string;
  /** Render a filter input inside the popover above the list */
  insetSearch?: boolean;
  /**
   * Override the minimum-selections threshold derived from the list component's static marker.
   * Used by VNDropdown to propagate the value through the autocomplete slot layer.
   */
  minSelections?: number;
}

const VNDropdownBase = ({
  options,
  value,
  onChange,
  onBlur,
  error,
  label,
  placeholder,
  disabled = false,
  multiple = false,
  displayComponent: DisplayComponent = VNDropdownDisplay,
  listComponent: ListComponent = VNDropdownList,
  inputRef,
  disableBorder = false,
  compact = false,
  popoverWidth,
  popoverMinHeight,
  className,
  insetSearch = false,
  minSelections: minSelectionsProp,
}: VNDropdownBaseProps) => {
  const minSelections: number = minSelectionsProp ?? (ListComponent as any).minSelections ?? 0;
  const uid = useId();
  const listboxId = `vn-dropdown-listbox${uid}`;
  const triggerId = `vn-dropdown-trigger${uid}`;
  const optionIdPrefix = `vn-dropdown-option${uid}`;

  const anchorRef = useRef<HTMLDivElement>(null);
  const insetSearchRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [popoverAlign, setPopoverAlign] = useState<'left' | 'right'>('left');
  const [insetQuery, setInsetQuery] = useState('');
  const [popoverMinWidth, setPopoverMinWidth] = useState(336);

  // Stable ref so the auto-select effect always calls the latest onChange without
  // needing it in the effect dependency array.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const displayedOptions = useMemo(
    () => (insetSearch ? filterOptionsWithAncestors(options, insetQuery) : options),
    [insetSearch, insetQuery, options]
  );

  // Reset focusedIndex to 0 whenever the inset query changes so the highlight
  // always sits on a visible item.
  useEffect(() => {
    if (insetSearch) setFocusedIndex(0);
  }, [insetSearch, insetQuery]);

  // Focus the inset search input when the popover opens.
  useEffect(() => {
    if (insetSearch && isOpen) {
      const id = setTimeout(() => insetSearchRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [insetSearch, isOpen]);

  const selectedIds: Array<DropdownOption['id']> = useMemo(() => {
    if (value === null || value === undefined) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  // Auto-select the first option whenever the selection is empty and a minimum
  // is required. On initial options load the restore is immediate; after a user
  // clears the last item it is deferred by 600 ms so the UI doesn't feel like
  // it is ignoring the interaction.
  const restoreTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const optionsLoadedRef = useRef(false);
  useEffect(() => {
    if (minSelections > 0 && multiple && options.length > 0) {
      if (selectedIds.length === 0) {
        const delay = optionsLoadedRef.current ? 200 : 0;
        restoreTimerRef.current = setTimeout(() => {
          onChangeRef.current(options.slice(0, minSelections).map((o) => o.id));
        }, delay);
      } else if (restoreTimerRef.current !== null) {
        clearTimeout(restoreTimerRef.current);
        restoreTimerRef.current = null;
      }
      optionsLoadedRef.current = true;
    }
    return () => {
      if (restoreTimerRef.current !== null) clearTimeout(restoreTimerRef.current);
    };
  }, [options, selectedIds, minSelections, multiple]);

  const selectedOptions = useMemo(() => options.filter((o) => selectedIds.includes(o.id)), [options, selectedIds]);

  const handleOpen = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const spaceRight = window.innerWidth - rect.right;
      setPopoverAlign(spaceRight < 320 ? 'right' : 'left');
      setPopoverMinWidth(compact ? 320 : Math.max(anchorRef.current.offsetWidth, 336));
    }
    // Pre-focus the first selected option, or the first item
    const firstSelectedIndex = displayedOptions.findIndex((o) => selectedIds.includes(o.id));
    setFocusedIndex(firstSelectedIndex >= 0 ? firstSelectedIndex : 0);
  }, [disabled, displayedOptions, selectedIds, compact]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setFocusedIndex(-1);
    setInsetQuery('');
    onBlur?.();
  }, [onBlur]);

  const handleToggle = useCallback(() => {
    if (isOpen) handleClose();
    else handleOpen();
  }, [isOpen, handleClose, handleOpen]);

  const handleSelect = useCallback(
    (option: DropdownOption) => {
      if (multiple) {
        const isSelected = selectedIds.includes(option.id);
        const next = isSelected ? selectedIds.filter((id) => id !== option.id) : [...selectedIds, option.id];
        onChange(next);
      } else {
        onChange(option.id);
        handleClose();
      }
    },
    [multiple, selectedIds, onChange, handleClose]
  );

  const handleClearOption = useCallback(
    (id: DropdownOption['id']) => {
      if (multiple) {
        onChange(selectedIds.filter((sid) => sid !== id));
      } else {
        onChange(null);
      }
    },
    [multiple, selectedIds, onChange]
  );

  const handleKeyDown = useCallback<React.KeyboardEventHandler<HTMLButtonElement>>(
    (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) handleOpen();
          else setFocusedIndex((prev) => Math.min(prev + 1, displayedOptions.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!isOpen) handleOpen();
          else setFocusedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (!isOpen) {
            handleOpen();
          } else if (focusedIndex >= 0 && focusedIndex < displayedOptions.length) {
            handleSelect(displayedOptions[focusedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          handleClose();
          break;
        case 'Tab':
          // Close on tab-out so focus management is clean
          handleClose();
          break;
      }
    },
    [isOpen, displayedOptions, focusedIndex, handleOpen, handleClose, handleSelect]
  );

  const handleInsetKeyDown = useCallback<React.KeyboardEventHandler<HTMLInputElement>>(
    (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => Math.min(prev + 1, displayedOptions.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < displayedOptions.length) {
            handleSelect(displayedOptions[focusedIndex]);
            if (!multiple) return; // handleSelect calls handleClose for single-select
            setInsetQuery('');
          }
          break;
        case 'Escape':
          e.preventDefault();
          handleClose();
          break;
        case 'Tab':
          handleClose();
          break;
      }
    },
    [displayedOptions, focusedIndex, handleSelect, handleClose, multiple]
  );

  const focusedOptionId = isOpen && focusedIndex >= 0 ? `${optionIdPrefix}-${focusedIndex}` : undefined;

  const displayProps = useMemo<DisplaySlotProps>(
    () => ({
      selectedOptions,
      isOpen,
      onToggle: handleToggle,
      onClearOption: handleClearOption,
      label,
      placeholder,
      disabled,
      error,
      multiple,
      inputRef,
      triggerId,
      listboxId,
      focusedOptionId,
      onKeyDown: handleKeyDown,
      disableBorder,
      compact,
    }),
    [
      selectedOptions,
      isOpen,
      handleToggle,
      handleClearOption,
      label,
      placeholder,
      disabled,
      error,
      multiple,
      inputRef,
      triggerId,
      listboxId,
      focusedOptionId,
      handleKeyDown,
      disableBorder,
      compact,
    ]
  );

  const listProps = useMemo<ListSlotProps>(
    () => ({
      options: displayedOptions,
      selectedIds,
      onSelect: handleSelect,
      onClose: handleClose,
      multiple,
      focusedIndex,
      listboxId,
      optionIdPrefix,
      insetQuery,
    }),
    [displayedOptions, selectedIds, handleSelect, handleClose, multiple, focusedIndex, listboxId, optionIdPrefix, insetQuery]
  );

  const popoverPaperSx = useMemo(
    () => ({
      minWidth: popoverWidth ?? popoverMinWidth,
      ...(popoverWidth !== undefined && { width: popoverWidth }),
      ...(popoverMinHeight !== undefined && { minHeight: popoverMinHeight }),
    }),
    [popoverWidth, popoverMinWidth, popoverMinHeight]
  );

  return (
    <div
      className={['vn-dropdown', compact ? 'vn-dropdown--compact' : '', className ?? ''].filter(Boolean).join(' ')}
      ref={anchorRef}
    >
      <DisplayComponent {...displayProps} />
      <Popover
        open={isOpen}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: popoverAlign }}
        transformOrigin={{ vertical: 'top', horizontal: popoverAlign }}
        disableScrollLock
        // Keep focus on the trigger so keyboard nav via aria-activedescendant works
        disableAutoFocus
        disableEnforceFocus
        PaperProps={{
          className: 'vn-dropdown__paper',
          sx: popoverPaperSx,
          elevation: 0,
        }}
      >
        {insetSearch && (
          <div className="vn-dropdown__inset-search">
            <div className="vn-dropdown__inset-search-field">
              <SearchIcon className="vn-dropdown__inset-search-icon" fontSize="small" aria-hidden="true" />
              <input
                ref={insetSearchRef}
                type="text"
                className="vn-dropdown__inset-search-input"
                placeholder="Search..."
                value={insetQuery}
                onChange={(e) => setInsetQuery(e.target.value)}
                onKeyDown={handleInsetKeyDown}
                autoComplete="off"
                aria-label="Filter options"
              />
            </div>
          </div>
        )}
        <ListComponent {...listProps} />
      </Popover>
    </div>
  );
};

export default VNDropdownBase;
